const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

/**
 * Converts a number to Arabic-Indic digits (٠١٢٣٤٥٦٧٨٩).
 * Use for displaying numbers in RTL/Arabic contexts across the site.
 * @param {number|string|null|undefined} n - The value to format
 * @param {Object} options - Intl.NumberFormat options (e.g. { maximumFractionDigits: 1 })
 * @returns {string} Formatted string with Arabic digits, or empty string for null/undefined
 */
export const toArabicDigits = (n, options = {}) => {
  if (n === null || n === undefined) return '';
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (Number.isNaN(num)) return String(n);
  return new Intl.NumberFormat('ar-EG', options).format(num);
};

/**
 * Replaces all Western digits (0-9) in a string with Arabic-Indic digits.
 * Use for dates, readTime ("5 دقائق"), and any mixed text containing numbers.
 * @param {string|null|undefined} str - The string to convert
 * @returns {string} String with Arabic digits, or empty string for null/undefined
 */
export const withArabicDigits = (str) => {
  if (str == null) return '';
  return String(str).replace(/\d/g, (d) => ARABIC_DIGITS[parseInt(d, 10)]);
};

/**
 * Formats a date string for display: DD-MM-YYYY with Arabic digits.
 * Day first, then month, then year (e.g. ٢١-٠٢-٢٠٢٦).
 * Handles YYYY-MM-DD, DD-MM-YYYY, and ISO strings.
 */
export const formatDateYYYYMMDD = (dateStr) => {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${withArabicDigits(d)}-${withArabicDigits(m)}-${withArabicDigits(y)}`;
  }
  const dmyMatch = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmyMatch) return withArabicDigits(s);
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return withArabicDigits(s);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${withArabicDigits(d)}-${withArabicDigits(m)}-${withArabicDigits(y)}`;
};

/**
 * Formats a date string as relative time in Arabic (e.g. "منذ ساعتين", "منذ ٣ أيام").
 */
export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'الآن';
  if (diffMins < 60) return `منذ ${toArabicDigits(diffMins)} دقيقة`;
  if (diffHours < 24) return `منذ ${toArabicDigits(diffHours)} ساعة`;
  if (diffDays < 30) return `منذ ${toArabicDigits(diffDays)} يوم`;
  return withArabicDigits(date.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' }));
};

